import { ReactNode } from 'react';

import { CodeEditor } from '@@/CodeEditor';

import { Values } from '../../types';
import { useMergedValuesFromStackID } from '../useMergedValuesFromStackID';

import { DiffViewMode } from './DiffControl';
import { DiffViewSection } from './DiffViewSection';
import { SelectedRevisionNumber, CompareRevisionNumberFetched } from './types';

interface Props {
  values?: Values;
  isUserSupplied: boolean;
  selectedRevisionNumber: SelectedRevisionNumber;
  diffViewMode: DiffViewMode;
  compareValues?: Values;
  compareRevisionNumberFetched?: CompareRevisionNumberFetched;
  isCompareReleaseLoading: boolean;
  isCompareReleaseError: boolean;
  diffControl: ReactNode;
  stackID?: number;
}

export function ValuesDetails({
  values,
  isUserSupplied,
  selectedRevisionNumber,
  diffViewMode,
  compareValues,
  compareRevisionNumberFetched,
  isCompareReleaseLoading,
  isCompareReleaseError,
  diffControl,
  stackID,
}: Props) {
  const gitValuesQuery = useMergedValuesFromStackID(stackID);
  const gitValues = gitValuesQuery.data;
  return (
    <>
      {diffControl}
      {diffViewMode === 'view' ? (
        <CodeEditor
          type="yaml"
          id="values-details-code-editor"
          data-cy="values-details-code-editor"
          value={
            isUserSupplied
              ? (values?.userSuppliedValues ?? '')
              : (values?.computedValues ?? '')
          }
          readonly
          fileName={`Revision #${selectedRevisionNumber}`}
          placeholder="No values found"
          height="60vh"
        />
      ) : (
        <DiffViewSection
          isCompareReleaseLoading={isCompareReleaseLoading}
          isCompareReleaseError={isCompareReleaseError}
          originalTitle={getOriginalTitle()}
          modifiedTitle={`Revision #${selectedRevisionNumber}`}
          newText={
            isUserSupplied
              ? (values?.userSuppliedValues ?? '')
              : (values?.computedValues ?? '')
          }
          originalText={getOriginalText()}
          id="values-details-diff-viewer"
          data-cy="values-details-diff-viewer"
        />
      )}
    </>
  );

  function getOriginalText() {
    if (diffViewMode === 'git' && isUserSupplied) {
      return gitValues?.mergedValues ?? '';
    }
    if (isUserSupplied) {
      return compareValues?.userSuppliedValues ?? '';
    }
    return compareValues?.computedValues ?? '';
  }

  function getOriginalTitle() {
    if (diffViewMode === 'git') {
      if (
        gitValues?.filesProcessed?.length &&
        gitValues?.filesProcessed?.length > 1
      ) {
        return gitValues?.filesProcessed?.join(', ');
      }
      if (gitValues?.filesProcessed?.length === 1) {
        return gitValues?.filesProcessed[0];
      }
      return 'No values configured';
    }
    return compareRevisionNumberFetched
      ? `Revision #${compareRevisionNumberFetched}`
      : 'No revision selected';
  }
}
